
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/base/Logo';
import Footer from '../../components/feature/Footer';

const ExtensionPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  const features = [
    {
      icon: 'ri-notification-3-line',
      title: 'Instant Price Alerts',
      description: 'Get notified the moment prices drop on products you\'re watching. Never miss a deal again.',
      color: 'from-teal-500 to-emerald-500',
      badge: 'Real-time',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Price History Charts',
      description: 'See complete price history for any product. Know if it\'s the right time to buy.',
      color: 'from-cyan-500 to-teal-500',
      badge: 'AI-Powered',
    },
    {
      icon: 'ri-store-2-line',
      title: 'Multi-Store Comparison',
      description: 'Compare prices across 500+ retailers instantly. Find the best deal every time.',
      color: 'from-emerald-500 to-green-500',
      badge: '500+ Stores',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Install Extension',
      description: 'Add Ohprice to Chrome in one click. It\'s free and takes 2 seconds.',
      icon: 'ri-download-cloud-line',
    },
    {
      number: '02',
      title: 'Shop Normally',
      description: 'Browse your favorite stores. The extension works automatically in the background.',
      icon: 'ri-shopping-bag-line',
    },
    {
      number: '03',
      title: 'See Price Insights',
      description: 'View price history, comparisons, and alerts right on the product page.',
      icon: 'ri-eye-line',
    },
    {
      number: '04',
      title: 'Save Money',
      description: 'Get notified when prices drop. Buy at the perfect time and save big.',
      icon: 'ri-money-dollar-circle-line',
    },
  ];

  const stats = [
    { value: '2M+', label: 'Active Users' },
    { value: '$47M', label: 'Saved by Users' },
    { value: '500+', label: 'Supported Stores' },
    { value: '10M+', label: 'Products Tracked' },
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Verified User',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20woman%20with%20friendly%20smile%2C%20natural%20lighting%2C%20neutral%20background%2C%20high%20quality%20photography&width=100&height=100&seq=ext-test1&orientation=squarish',
      content: 'Saved $340 on my new laptop! The price history showed me it was at an all-time low.',
      rating: 5,
    },
    {
      name: 'Michael R.',
      role: 'Verified User',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20middle%20aged%20man%20with%20glasses%2C%20friendly%20expression%2C%20neutral%20background%2C%20high%20quality%20photography&width=100&height=100&seq=ext-test2&orientation=squarish',
      content: 'I use this for every purchase now. The price alerts have saved me hundreds.',
      rating: 5,
    },
    {
      name: 'Emily K.',
      role: 'Verified User',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20young%20professional%20woman%2C%20warm%20smile%2C%20soft%20lighting%2C%20neutral%20background%2C%20high%20quality%20photography&width=100&height=100&seq=ext-test3&orientation=squarish',
      content: 'Finally, a price tracker that actually works! Love seeing the price history on Amazon.',
      rating: 5,
    },
  ];

  const supportedStores = [
    { name: 'Amazon', icon: 'ri-amazon-fill' },
    { name: 'Best Buy', icon: 'ri-store-2-line' },
    { name: 'Walmart', icon: 'ri-store-line' },
    { name: 'Target', icon: 'ri-focus-2-line' },
    { name: 'eBay', icon: 'ri-auction-line' },
    { name: 'Newegg', icon: 'ri-computer-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" variant="light" linkTo="/" />
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-white/80 hover:text-white transition-colors cursor-pointer">Home</Link>
              <Link to="/shop" className="text-white/80 hover:text-white transition-colors cursor-pointer">Compare Prices</Link>
              <Link to="/deals" className="text-white/80 hover:text-white transition-colors cursor-pointer">Deals</Link>
              <a href="#features" className="text-white/80 hover:text-white transition-colors cursor-pointer">Features</a>
            </nav>
            <a
              href="#download"
              className="px-5 py-2.5 bg-white text-teal-600 rounded-full font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Extension
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-700 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-400/10 rounded-full blur-3xl"></div>
        </div>

        {/* Floating Price Tags */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-32 left-[15%] bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-medium animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
            -30% OFF
          </div>
          <div className="absolute top-48 right-[20%] bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-medium animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}>
            -50% OFF
          </div>
          <div className="absolute bottom-40 left-[25%] bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-white text-sm font-medium animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
            -25% OFF
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-200px)]">
            {/* Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm mb-6">
                <i className="ri-chrome-line"></i>
                <span>Free Chrome Extension</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Never <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-300">Overpay</span> Again
              </h1>
              <p className="text-xl text-white/80 mb-8 max-w-lg mx-auto lg:mx-0">
                Track prices on 10M+ products across 500+ stores. Get instant alerts when prices drop.
              </p>

              {/* Trust Badge */}
              <div className="flex items-center gap-3 justify-center lg:justify-start mb-8">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white overflow-hidden">
                      <img
                        src={`https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20a%20person%20with%20friendly%20smile%2C%20high%20quality%20photography%2C%20neutral%20background&width=80&height=80&seq=ext-user${i}&orientation=squarish`}
                        alt="User"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="text-white">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(i => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-white/80">Join 2M+ smart shoppers</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" id="download">
                <a
                  href="#"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-teal-600 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-chrome-line text-2xl"></i>
                  Add to Chrome - Free
                </a>
                <a
                  href="#demo"
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-white/30 text-white rounded-full font-medium hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-xl"></i>
                  Watch Demo
                </a>
              </div>
            </div>

            {/* Browser Mockup */}
            <div className="relative hidden lg:block">
              <div className="relative transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                  {/* Browser Chrome */}
                  <div className="bg-gray-100 px-4 py-3 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="flex-1 bg-white rounded-lg px-4 py-1.5 text-sm text-gray-500 flex items-center gap-2">
                      <i className="ri-lock-line text-green-500"></i>
                      amazon.com/product/...
                    </div>
                  </div>
                  {/* Extension Popup */}
                  <div className="relative">
                    <img
                      src="https://readdy.ai/api/search-image?query=E-commerce%20product%20page%20showing%20a%20laptop%20computer%20with%20price%20and%20buy%20button%2C%20clean%20modern%20web%20design%2C%20Amazon%20style%20product%20listing%20page&width=600&height=400&seq=ext-browser&orientation=landscape"
                      alt="Browser"
                      className="w-full"
                    />
                    {/* Extension Overlay */}
                    <div className="absolute top-4 right-4 bg-white rounded-xl shadow-2xl p-4 w-64 animate-pulse">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
                          <i className="ri-price-tag-3-line text-white"></i>
                        </div>
                        <span className="font-semibold text-gray-900">Ohprice</span>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                          <i className="ri-arrow-down-line"></i>
                          Price dropped 23%!
                        </div>
                        <p className="text-xs text-green-600 mt-1">Lowest price in 30 days</p>
                      </div>
                      <div className="h-16 bg-gray-50 rounded-lg flex items-center justify-center">
                        <div className="text-xs text-gray-400">Price History Chart</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Store Logos Ticker */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-sm py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-12 opacity-70">
              {supportedStores.map((store, idx) => (
                <div key={idx} className="flex items-center gap-2 text-white">
                  <i className={`${store.icon} text-2xl`}></i>
                  <span className="text-sm font-medium hidden sm:inline">{store.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Powerful Features</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Everything You Need to Save
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Automated price tracking that works while you browse
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group bg-white rounded-3xl p-8 border border-gray-100 hover:border-teal-200 hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${feature.color} text-white mb-4`}>
                  {feature.badge}
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Simple Setup</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Start Saving in 4 Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                {idx < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-full h-0.5 bg-gradient-to-r from-teal-300 to-transparent"></div>
                )}
                <div className="text-center">
                  <div className="relative inline-block mb-6">
                    <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                      <i className={`${step.icon} text-4xl text-teal-600`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {step.number.slice(-1)}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
              Loved by Smart Shoppers
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-50 rounded-2xl p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm mb-6 animate-pulse">
            <i className="ri-time-line"></i>
            <span>Limited Time - 100% Free</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Start Saving Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join millions of smart shoppers who save <em className="font-semibold">automatically</em>, get alerts <em className="font-semibold">instantly</em>, and shop <em className="font-semibold">effortlessly</em>.
          </p>
          <a
            href="#"
            className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-gray-900 text-white rounded-full font-semibold text-lg hover:bg-gray-800 transition-all shadow-xl hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chrome-line text-2xl"></i>
            Add Extension - It's Free
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-white/70 text-sm mt-4">No credit card required • 2-minute setup</p>
        </div>
      </section>

      {/* Notify Me Section */}
      <section id="demo" className="py-24 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-firefox-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Firefox & Safari Coming Soon
          </h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            We're working on bringing Ohprice to more browsers. Get notified when we launch.
          </p>
          {isSubmitted ? (
            <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-6 py-3 rounded-full">
              <i className="ri-check-circle-fill"></i>
              <span>You're on the list!</span>
            </div>
          ) : (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-5 py-3 bg-white/10 border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-full font-medium hover:from-teal-600 hover:to-emerald-600 cursor-pointer whitespace-nowrap"
              >
                Notify Me
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExtensionPage;
